import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'

import {
  MEETINGS_TAG_TYPES,
  buildMeetingListTag,
  buildMeetingTag,
} from './constants'
import type {
  MeetingsGraphQLResult,
  GraphQLMeetingResult,
} from './types'

export type SupergraphMeetingsInput = any
/** `meetings_search` (search-api + cmd-api) */
export type SupergraphMeetingsSearchInput = any
export type SupergraphMeetingsConnection = MeetingsGraphQLResult
export type SupergraphMeeting = GraphQLMeetingResult
export type SupergraphGetMeetingArg = { id: string; organizationUuid: string }

/** From `SUPERGRAPH_BASE_URL` at webpack build (`define.mjs`); else `/api/supergraph/graphql`. */
export const getSupergraphBaseUrl = () => {
  const value = typeof window !== 'undefined'
    && (window as any).SUPERGRAPH_BASE_URL

  return (value && String(value)) || '/api/supergraph/graphql'
}

export const transformMeetingsSupergraphConnectionResponse = (response: any) => {
  const conn = response?.meetings_search
    ?? response?.getMeetings
    ?? response?.meetings
    ?? {}
  const rawEdges = Array.isArray(conn.edges) ? conn.edges : []
  const edges = rawEdges
    .filter((edge: any) => edge?.node != null)
    .map((edge: any) => ({
      node: {
        ...edge.node,
        key:
          typeof crypto !== 'undefined'
          && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : edge.node.uuid,
      },
    }))

  return {
    meetings: {
      ...conn,
      edges,
    },
  }
}

export const transformGetMeetingSupergraphResponse = (response: any) => (
  response?.getMeetingById ?? null
)

const GetMeetingsCountDocument = `
  query GetMeetingsCount($input: Meetings_MeetingsInput!) {
    getMeetings(input: $input) {
      total
    }
  }
`

// istanbul ignore next
const GetMeetingsDocument = `
  query GetMeetings($input: Meetings_MeetingsInput!) {
    getMeetings(input: $input) {
      total
      edges {
        cursor
        node {
          uuid
          kind
          owner_user_uuid
          name
          start_time
          end_time
          location
          location_kind
          cancelled
          status
          is_notetaker_eligible

          meeting_intelligence {
            uuid
            status
            error
          }

          attendees {
            kind
            name
            email
            timezone
            ... on Meetings_InviteeAttendee {
              uuid
              rsvp
              cancelled
            }
            ... on Meetings_HostAttendee {
              uuid
            }
            ... on Meetings_CoHostAttendee {
              uuid
            }
          }

          ... on Meetings_CalendlyMeeting {
            organization_uuid
            co_hosts { uuid }
            event_type {
              uuid
              name
              color
              duration
              kind
              type
              owner_type
              invitees_limit
            }
            invitees_count
            invitees_limit
            filled_count
          }

          ... on Meetings_GoogleMeeting {
            is_organizer
          }

          ... on Meetings_OutlookMeeting {
            is_organizer
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

// istanbul ignore next
const GetMeetingDocument = `
  query GetMeeting($id: ID!, $organizationUuid: ID!) {
    getMeetingById(id: $id, organization_uuid: $organizationUuid) {
      uuid
      kind
      owner_user_uuid
      name
      start_time
      end_time
      location
      location_kind
      cancelled
      status
      is_notetaker_eligible

      meeting_intelligence {
        uuid
        status
        error
      }

      attendees {
        kind
        name
        email
        timezone
        ... on Meetings_InviteeAttendee {
          uuid
          rsvp
          cancelled
        }
        ... on Meetings_HostAttendee {
          uuid
        }
        ... on Meetings_CoHostAttendee {
          uuid
        }
      }

      ... on Meetings_CalendlyMeeting {
        organization_uuid
        co_hosts { uuid }
        event_type {
          uuid
          name
          color
          duration
          kind
          type
          owner_type
          invitees_limit
        }
        invitees_count
        invitees_limit
        filled_count
      }

      ... on Meetings_GoogleMeeting {
        is_organizer
      }

      ... on Meetings_OutlookMeeting {
        is_organizer
      }
    }
  }
`

// istanbul ignore next
const SearchMeetingsSupergraphDocument = `
  query SearchMeetingsSupergraph($input: MeetingsSearchInput!) {
    meetings_search(input: $input) {
      total
      edges {
        cursor
        node {
          uuid
          kind
          owner_user_uuid
          name
          start_time
          end_time
          location
          location_kind
          cancelled
          status
          is_notetaker_eligible

          meeting_intelligence {
            uuid
            status
            error
          }

          attendees {
            kind
            name
            email
            timezone
            ... on Meetings_InviteeAttendee {
              uuid
              rsvp
              cancelled
            }
            ... on Meetings_HostAttendee {
              uuid
            }
            ... on Meetings_CoHostAttendee {
              uuid
            }
          }

          ... on Meetings_CalendlyMeeting {
            organization_uuid
            co_hosts { uuid }
            event_type {
              uuid
              name
              color
              duration
              kind
              type
              owner_type
              invitees_limit
            }
            invitees_count
            invitees_limit
            filled_count
          }

          ... on Meetings_GoogleMeeting {
            is_organizer
          }

          ... on Meetings_OutlookMeeting {
            is_organizer
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

// istanbul ignore next
const SearchMeetingsSupergraphCountDocument = `
  query SearchMeetingsSupergraphCount($input: MeetingsSearchInput!) {
    meetings_search(input: $input) {
      total
    }
  }
`

const transformSupergraphFilterCountResponse = (response: any) => (
  response?.meetings_search?.total
  ?? response?.getMeetings?.total
  ?? response?.meetings?.total
  ?? 0
)

export const meetingsSupergraphApi = rootApi
  .enhanceEndpoints({ addTagTypes: MEETINGS_TAG_TYPES })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      getMeetingsSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphMeetingsInput
      >({
        query: (input: SupergraphMeetingsInput) => ({
          document: GetMeetingsDocument,
          variables: { input },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      searchMeetingsSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphMeetingsSearchInput
      >({
        query: (input: SupergraphMeetingsSearchInput) => ({
          document: SearchMeetingsSupergraphDocument,
          variables: { input },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      getMeetingsSupergraphFilterCount: build.query<
        number,
        SupergraphMeetingsInput
      >({
        query: (input: SupergraphMeetingsInput) => ({
          document: GetMeetingsCountDocument,
          variables: { input },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformSupergraphFilterCountResponse,
        providesTags: [buildMeetingListTag()],
      }),

      searchMeetingsSupergraphFilterCount: build.query<
        number,
        SupergraphMeetingsSearchInput
      >({
        query: (input: SupergraphMeetingsSearchInput) => ({
          document: SearchMeetingsSupergraphCountDocument,
          variables: { input },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformSupergraphFilterCountResponse,
        providesTags: [buildMeetingListTag()],
      }),

      getMeetingSupergraph: build.query<
        SupergraphMeeting | null,
        SupergraphGetMeetingArg
      >({
        query: ({ id, organizationUuid }) => ({
          document: GetMeetingDocument,
          variables: { id, organizationUuid },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetMeetingSupergraphResponse,
        providesTags: (result, _error, args) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.id) {
            tags.push(buildMeetingTag(args.id))
          }
          return tags
        },
        extraOptions: { handleErrors: false },
      }),
    }),
  })

export const {
  useGetMeetingsSupergraphQuery,
  useGetMeetingsSupergraphFilterCountQuery,
  useLazyGetMeetingsSupergraphFilterCountQuery,
  useLazyGetMeetingsSupergraphQuery,
  useSearchMeetingsSupergraphQuery,
  useSearchMeetingsSupergraphFilterCountQuery,
  useGetMeetingSupergraphQuery,
  useLazyGetMeetingSupergraphQuery,
} = meetingsSupergraphApi
